import axios from 'axios';
import _ from 'lodash';

import {
	LOADING_MORE_CUSTOMER_CONVERSATIONS,
	SET_CUSTOMER_CONVERSATIONS,
	OPEN_CONVERSATIONS_SELECTED,
	SET_CUSTOMER_CONVERSATION_ASSIGNED_TYPE,
	SHOW_ONLY_UNREAD_CONVERSATIONS,
	SET_CUSTOMER_CONVERSATION_CHANNEL_FILTER,
	SET_CUSTOMER_CONVERSATIONS_LAST_SYNCED_AT,
	UPDATE_CUSTOMER_CONVERSATION,
	UPSERT_CUSTOMER_CONVERSATION,
	REMOVE_CUSTOMER_CONVERSATION,
	LOADING,
	SET_ACTIVE_CONVERSATION,
	SET_SOCKET_ID,
	ACTIVE_DASHBOARD,
	SET_BUSINESS_GROUP,
	ADD_OPTIMISTIC_MESSAGE,
	FAIL_OPTIMISTIC_MESSAGE,
	REMOVE_OPTIMISTIC_MESSAGE,
	SET_COMPOSER_RESTORE,
	CLEAR_COMPOSER_RESTORE,
} from './types';

import getWebchatApiUrl from '../utils/getWebchatUrl';

import { getFilteredCustomerConversationsFromSelectionState } from '@/utils/webchat/getFilteredCustomerConversationsFromSelectionState';
import { hydrateAiDraftsFromConversations } from '@/slices/aiEmployeeSlice';

const getEarliestConversationDate = (state, _user) => {
	// use getFilteredCustomerConversationsByAssignedTypeAndOpenState to get the latest conversation date
	// assignedTypeSelected: "ALL" || "UNASSIGNED" || "MY_MESSAGES" || "USER_id"

	const filteredConversations =
		getFilteredCustomerConversationsFromSelectionState({ ...state, _user });

	const earliestConversationDate = new Date(
		Math.min.apply(
			null,
			filteredConversations.map((e) => new Date(e.latestMessageDate))
		)
	);

	return earliestConversationDate;
};

const getMostRecentConversationDate = (state, _user) => {
	const filteredConversations =
		getFilteredCustomerConversationsFromSelectionState({ ...state, _user });
	const conversationDates = filteredConversations
		.map((conversation) => new Date(conversation.latestMessageDate).getTime())
		.filter((date) => Number.isFinite(date));

	if (!conversationDates.length) {
		return null;
	}

	return new Date(Math.max(...conversationDates)).toISOString();
};

const getCatchUpChangedSince = (state, _user) => {
	const mostRecentConversationDate = getMostRecentConversationDate(state, _user);
	const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);

	if (!mostRecentConversationDate) {
		return sevenDaysAgo.toISOString();
	}

	const mostRecentDate = new Date(mostRecentConversationDate);
	// Bound fallback catch-up to seven days, but start at the newest local
	// conversation when we have recent data to avoid re-querying known history.
	const catchUpDate =
		mostRecentDate > sevenDaysAgo ? mostRecentDate : sevenDaysAgo;

	return catchUpDate.toISOString();
};

const mergeConversationWithLocalMessages = (
	incomingConversation,
	localConversation
) => {
	if (!localConversation || !incomingConversation) {
		return incomingConversation;
	}

	if (
		incomingConversation.messagesPreviewOnly &&
		localConversation.messages &&
		!localConversation.messagesPreviewOnly
	) {
		return {
			...incomingConversation,
			messages: localConversation.messages,
			messagesPreviewOnly: false,
		};
	}

	const incomingMessages = incomingConversation.messages || [];
	const localOptimisticMessages = (localConversation.messages || []).filter(
		(message) => message?.clientId
	);

	if (!localOptimisticMessages.length) {
		return incomingConversation;
	}

	const incomingMessageKeys = new Set(
		incomingMessages
			.map((message) => message?.clientId || message?._id)
			.filter(Boolean)
	);
	const missingOptimisticMessages = localOptimisticMessages.filter(
		(message) => !incomingMessageKeys.has(message.clientId || message._id)
	);

	if (!missingOptimisticMessages.length) {
		return incomingConversation;
	}

	return {
		...incomingConversation,
		messages: _.sortBy(
			[...incomingMessages, ...missingOptimisticMessages],
			(message) =>
				new Date(message.createdAt || message.updatedAt || 0).getTime()
		),
	};
};

const getPinnedUserIds = (conversation) =>
	(conversation?._pinnedByUsers || []).map((_user) => _user.toString());

const getConversationWithPinState = ({ conversation, scope, pinned, _user }) => {
	if (scope === 'user') {
		const pinnedUserIds = getPinnedUserIds(conversation);
		const nextPinnedUserIds = pinned
			? _.uniq([...pinnedUserIds, _user])
			: pinnedUserIds.filter((_pinnedUser) => _pinnedUser !== _user);

		return {
			...conversation,
			_pinnedByUsers: nextPinnedUserIds,
		};
	}

	return {
		...conversation,
		pinnedForBusiness: pinned,
		_pinnedForBusinessBy: pinned ? _user : null,
		pinnedForBusinessAt: pinned ? new Date().toISOString() : null,
	};
};

// Optimistic-send action creators.
// ChatBox dispatches add on send; WebchatDashboard.sendMessage dispatches
// remove on success and fail on error; ChatFeed dispatches remove + setComposerRestore
// when the user clicks a failed bubble.
export const addOptimisticMessage = ({ _conversation, message }) => ({
	type: ADD_OPTIMISTIC_MESSAGE,
	payload: { _conversation, message },
});

export const failOptimisticMessage = ({
	_conversation,
	clientId,
	errorMessage,
}) => ({
	type: FAIL_OPTIMISTIC_MESSAGE,
	payload: { _conversation, clientId, errorMessage },
});

export const removeOptimisticMessage = ({ _conversation, clientId }) => ({
	type: REMOVE_OPTIMISTIC_MESSAGE,
	payload: { _conversation, clientId },
});

export const setComposerRestore = (payload) => ({
	type: SET_COMPOSER_RESTORE,
	payload,
});

export const clearComposerRestore = () => ({ type: CLEAR_COMPOSER_RESTORE });

export const setCustomerConversations = (conversations) => (dispatch) => {
	dispatch(hydrateAiDraftsFromConversations(conversations));
	dispatch({
		type: SET_CUSTOMER_CONVERSATIONS,
		payload: conversations,
	});
};

export const setWebchatSocketId = (socketId) => (dispatch) => {
	dispatch({
		type: SET_SOCKET_ID,
		payload: socketId,
	});
};

const setCustomerConversationsLastSyncedAt = () => ({
	type: SET_CUSTOMER_CONVERSATIONS_LAST_SYNCED_AT,
	payload: Date.now(),
});

// Get next (or first) 20 customer data
// startFromConversationDate must be a new Date object
// options: initial: bool => look ALL messages (e.g. no UI state is selected, so don't filter for open/closed/all/my_messages etc)
export const getCustomerConversations =
	(
		options = {
			initial: false,
			channelFilter: undefined,
			_customerConversations: undefined,
		}
	) =>
	async (dispatch, getState) => {
		try {
			const { initial } = options;

			// if initial is true, set master loader
			if (initial) {
				dispatch({
					type: LOADING,
					payload: true,
				});
			}

			dispatch({
				type: LOADING_MORE_CUSTOMER_CONVERSATIONS,
				payload: true,
			});

			const state = { ...getState().webchat };
			const user = getState().auth.user;

			const {
				customerConversations = [],
				openConversationsSelected,
				assignedTypeSelected,
				showOnlyUnreadConversations,
			} = state;

			const earliestConversationDate = initial
				? null
				: getEarliestConversationDate(state, user._id);

			const conversationRes = await axios.post(
				`${getWebchatApiUrl(
					window.location.href
				)}/api/webchat/customer-conversations`,
				{
					earliestConversationDate,
					openConversationsSelected,
					assignedTypeSelected,
					isEnterpriseRequested: user.isEnterpriseEnabled,
					showOnlyUnreadConversations,
					channelFilter: options.channelFilter,
					_customerConversations: options._customerConversations,
					previewOnly: true,
				}
			);

			dispatch({
				type: LOADING,
				payload: false,
			});

			dispatch({
				type: LOADING_MORE_CUSTOMER_CONVERSATIONS,
				payload: false,
			});

			if (conversationRes.data && conversationRes.data.refresh) {
				return {
					logout: true,
				};
			}

			const newConversations = conversationRes.data.customerConversations;

			if (!newConversations) {
				return {
					displayNotification: true,
					message:
						'Error getting your conversations. Please refresh the page and try again.',
					error: true,
				};
			}

			const numberOfClosedConversations = newConversations.filter(
				(conversation) => !conversation.open
			).length;

			const numberOfOpenConversations =
				newConversations.length - numberOfClosedConversations;

			const newConversationCount = openConversationsSelected
				? numberOfOpenConversations
				: numberOfClosedConversations;

			const allConversations = [
				...newConversations,
				...customerConversations,
			];

			// make sure there are no duplicates
			const uniqueConversations = _.uniqBy(allConversations, '_id');

			dispatch(hydrateAiDraftsFromConversations(uniqueConversations));
			dispatch({
				type: SET_CUSTOMER_CONVERSATIONS,
				payload: uniqueConversations,
			});
			dispatch(setCustomerConversationsLastSyncedAt());

			const { activeConversation } = state;

			if (
				activeConversation &&
				uniqueConversations.find(
					(conversation) =>
						conversation._id === activeConversation._id
				)
			) {
				dispatch(setActiveConversation(activeConversation._id));
			}

			// notify customer of how many new conversations were loaded
			if (!initial) {
				if (newConversations && newConversations.length) {
					return {
						displayNotification: true,
						message: `Found ${newConversationCount} previous conversations`,
						error: false,
					};
				}
			}
		} catch (err) {
			console.log('Error getting customer conversations', err);
			dispatch({
				type: LOADING_MORE_CUSTOMER_CONVERSATIONS,
				payload: false,
			});
			return {
				displayNotification: true,
				message:
					'Error getting your conversations. Please refresh the page and try again.',
				error: true,
			};
		}
	};

export const syncCustomerConversationsAfterIdle =
	(options = {}) =>
	async (dispatch, getState) => {
		try {
			const state = { ...getState().webchat };
			const user = getState().auth.user;
			const {
				customerConversations = [],
				activeConversation,
				activeDashboard,
			} = state;

			if (activeDashboard && activeDashboard !== 'webchat') {
				return { error: false, skipped: true };
			}

			const changedSince =
				options.changedSince || getCatchUpChangedSince(state, user._id);
			const visibleConversationIds = customerConversations.map(
				(conversation) => conversation._id
			);

			const conversationRes = await axios.post(
				`${getWebchatApiUrl(
					window.location.href
				)}/api/webchat/conversations-changed-since`,
				{
					changedSince,
					isEnterpriseRequested: user.isEnterpriseEnabled,
					visibleConversationIds,
					includeVisibleMetadata: true,
					newConversationLimit: options.newConversationLimit || 100,
					previewOnly: true,
				}
			);

			if (conversationRes.data?.refresh) {
				return { logout: true };
			}

			const newConversations =
				conversationRes.data?.customerConversations;

			if (!newConversations) {
				return {
					displayNotification: true,
					message:
						'Error refreshing your conversations. Please refresh the page and try again.',
					error: true,
				};
			}

			dispatch(hydrateAiDraftsFromConversations(newConversations));
			newConversations.forEach((incomingConversation) => {
				const localConversation =
					getState().webchat.customerConversations.find(
						(conversation) =>
							conversation._id === incomingConversation._id
					);
				const mergedConversation = mergeConversationWithLocalMessages(
					incomingConversation,
					localConversation
				);

				dispatch({
					type: UPSERT_CUSTOMER_CONVERSATION,
					payload: mergedConversation,
				});
			});
			dispatch(setCustomerConversationsLastSyncedAt());

			if (newConversations.some((conversation) => !conversation.isRead)) {
				const { businessGroup } = getState();
				if (Array.isArray(businessGroup)) {
					const unreadBusinessIds = new Set(
						newConversations
							.filter((conversation) => !conversation.isRead)
							.map((conversation) => conversation._business)
					);

					dispatch({
						type: SET_BUSINESS_GROUP,
						payload: businessGroup.map((biz) =>
							unreadBusinessIds.has(biz._id)
								? { ...biz, hasUnreadMessages: true }
								: biz
						),
					});
				}
			}

			const currentActiveConversation =
				getState().webchat.activeConversation || activeConversation;
			const activeCustomerId =
				currentActiveConversation?._customer?._id ||
				currentActiveConversation?._customer;
			if (
				currentActiveConversation &&
				currentActiveConversation._id &&
				activeCustomerId
			) {
				await dispatch(
					refreshCustomerConversation({
						_customer: activeCustomerId,
					})
				);
			} else if (currentActiveConversation?.isGroup) {
				// group conversations have no _customer — the catch-up merge above is
				// preview-only, so fetch the open thread's full messages by id
				const refreshRes = await axios(
					`${getWebchatApiUrl(
						window.location.href
					)}/api/webchat/get-messages-from-customer`,
					{
						method: 'post',
						data: { _customerConversation: currentActiveConversation._id },
						withCredentials: true,
					}
				);
				if (refreshRes.data && !refreshRes.data.error) {
					await dispatch(
						addConversationToCustomerConversations(
							refreshRes.data.customerConversation
						)
					);
				}
			}

			return {
				error: false,
				updatedCount: newConversations.length,
				serverTime: conversationRes.data?.serverTime,
			};
		} catch (err) {
			console.log('Error syncing customer conversations after idle', err);
			return {
				displayNotification: true,
				message:
					'Error refreshing your conversations. Please refresh the page and try again.',
				error: true,
			};
		}
	};

export const setOpenOrClosedConversationsSelected =
	(openConversationsSelected) => async (dispatch) => {
		dispatch({
			type: OPEN_CONVERSATIONS_SELECTED,
			payload: openConversationsSelected,
		});
	};

// newType === "ALL" || "UNASSIGNED" || "MY_MESSAGES" || "CONTACTS" || "USER_" || "DEPARTMENT_" || "LOCATION_" || "GROUPS" || "DIRECT_MESSAGES" || "NEW_CHAT" || "FOLDER_"
export const setCustomerConversationAssignedType =
	(newStatus) => async (dispatch) => {
		dispatch({
			type: SET_CUSTOMER_CONVERSATION_ASSIGNED_TYPE,
			payload: newStatus,
		});
	};

export const setShowOnlyUnreadConversations =
	(showOnlyUnreadConversations) => async (dispatch) => {
		dispatch({
			type: SHOW_ONLY_UNREAD_CONVERSATIONS,
			payload: showOnlyUnreadConversations,
		});
	};

export const setCustomerConversationChannelFilter =
	(channelFilter) => async (dispatch) => {
		dispatch({
			type: SET_CUSTOMER_CONVERSATION_CHANNEL_FILTER,
			payload: channelFilter === 'All' ? undefined : channelFilter,
		});
	};

export const setConversationToRead =
	({ conversation }) =>
	async (dispatch, getState) => {
		// get user from redux state to see if they have own is read indicators
		const user = getState().auth.user;

		// if user has own is read indicators, check if the conversation is already read by the user
		if (user.hasOwnIsReadIndicators) {
			if (
				conversation &&
				conversation._readByUsers &&
				conversation._readByUsers.includes(user._id)
			) {
				return;
			}
		} else if (conversation && conversation.isRead) {
			return;
		}

		const conversationMarkedAsRead = {
			...conversation,
			isRead: true,
			_readByUsers: [user._id],
		};

		dispatch({
			type: UPDATE_CUSTOMER_CONVERSATION,
			payload: conversationMarkedAsRead,
		});

		const { webchat, businessGroup } = getState();
		const hasRemainingUnread = webchat.customerConversations.some(
			(c) => c._business === conversation._business && !c.isRead && c._id !== conversation._id
		);

		if (!hasRemainingUnread) {
			dispatch({
				type: SET_BUSINESS_GROUP,
				payload: businessGroup.map((biz) =>
					biz._id === conversation._business
						? { ...biz, hasUnreadMessages: false }
						: biz
				),
			});
		}

		axios(
			`${getWebchatApiUrl(
				window.location.href
			)}/api/webchat/set-conversation-as-read`,
			{
				method: 'post',
				data: {
					_customerConversation: conversation._id,
					_business: conversation._business,
				},
				withCredentials: true,
			}
		);
	};

export const toggleMarkAsReadConversation =
	({ conversation, isRead }) =>
	(dispatch, getState) => {
		const user = getState().auth.user;

		axios.post(
			`${getWebchatApiUrl(
				window.location.href
			)}/api/conversation/toggle-isRead`,
			{
				_conversation: conversation._id,
				_business: conversation._business,
				isRead,
			}
		);

		const newConversation = {
			...conversation,
			isRead,
			_readByUsers: isRead ? [user._id] : [],
		};

		dispatch({
			type: UPDATE_CUSTOMER_CONVERSATION,
			payload: newConversation,
		});

		const { webchat, businessGroup } = getState();

		if (isRead) {
			const hasRemainingUnread = webchat.customerConversations.some(
				(c) => c._business === conversation._business && !c.isRead && c._id !== conversation._id
			);

			if (!hasRemainingUnread) {
				dispatch({
					type: SET_BUSINESS_GROUP,
					payload: businessGroup.map((biz) =>
						biz._id === conversation._business
							? { ...biz, hasUnreadMessages: false }
							: biz
					),
				});
			}
		} else {
			dispatch({
				type: SET_BUSINESS_GROUP,
				payload: businessGroup.map((biz) =>
					biz._id === conversation._business
						? { ...biz, hasUnreadMessages: true }
						: biz
				),
			});
		}
	};

export const setActiveConversation = (_conversation) => (dispatch) => {
	if (typeof _conversation === 'string') {
		dispatch({
			type: SET_ACTIVE_CONVERSATION,
			payload: _conversation,
		});
	}

	if (typeof _conversation === 'object') {
		dispatch({
			type: SET_ACTIVE_CONVERSATION,
			payload: _conversation._id,
		});
	}
};

export const toggleConversationOpen =
	({ conversation, open }) =>
	(dispatch) => {
		const newConversation = { ...conversation, open };

		dispatch({
			type: UPDATE_CUSTOMER_CONVERSATION,
			payload: newConversation,
		});

		axios.post(
			`${getWebchatApiUrl(
				window.location.href
			)}/api/conversation/toggle-open`,
			{
				_conversation: conversation._id,
				_business: conversation._business,
				open,
			}
		);
	};

// The newest AI on/off request per conversation. Answers can arrive out of
// order, and only the answer to the newest request may change the screen.
const latestAiPauseRequest = new Map();
const sendAiPause = (_customerConversation, body) => (dispatch) => {
	const request = (latestAiPauseRequest.get(_customerConversation) || 0) + 1;
	latestAiPauseRequest.set(_customerConversation, request);
	return axios
		.put(`/api/customer-conversations/${_customerConversation}/ai-pause`, body)
		.then((res) => {
			if (latestAiPauseRequest.get(_customerConversation) === request) {
				dispatch(showStoredAiPause(_customerConversation, res.data));
			}
			return res.data;
		})
		.catch(() => undefined);
};

// Shows what the server stored for the AI on a conversation. The screen changes
// before the server answers, and the server may keep what was there (a
// teammate's hold, a pause that was already set).
export const showStoredAiPause = (_customerConversation, stored) => (dispatch, getState) => {
	if (!stored || typeof stored.aiPaused !== 'boolean') return;
	const conv = getState().webchat.customerConversations.find(
		(item) => item._id === _customerConversation
	);
	if (!conv) return;
	dispatch({
		type: UPDATE_CUSTOMER_CONVERSATION,
		payload: {
			...conv,
			aiPaused: stored.aiPaused,
			aiPauseReason: stored.aiPauseReason ?? null,
			handoffStatus: stored.handoffStatus ?? conv.handoffStatus,
		},
	});
};

// `reason` is 'human_reply' for the pause that follows a reply, which lifts on its
// own once the conversation is closed and quiet. Without it, pausing is a team
// member switching the AI off by hand, which stays until they switch it back on.
export const toggleConversationAiPause =
	({ conversation, aiPaused, reason }) =>
	(dispatch, getState) => {
		const latestConversation =
			getState().webchat.customerConversations.find(
				(item) => item._id === conversation._id
			) || conversation;
		if (latestConversation.aiPaused === aiPaused) return false;

		// What the server records: switching the AI off by hand is a hold.
		const newConversation = {
			...latestConversation,
			aiPaused,
			aiPauseReason: aiPaused ? reason || 'manual_hold' : null,
		};

		dispatch({
			type: UPDATE_CUSTOMER_CONVERSATION,
			payload: newConversation,
		});

		dispatch(
			sendAiPause(conversation._id, reason ? { aiPaused, reason } : { aiPaused })
		);

		return true;
	};

export const toggleConversationPin =
	({ conversation, scope, pinned }) =>
	async (dispatch, getState) => {
		const user = getState().auth.user;
		const _user = user._id;
		const updatedConversation = getConversationWithPinState({
			conversation,
			scope,
			pinned,
			_user,
		});

		dispatch({
			type: UPDATE_CUSTOMER_CONVERSATION,
			payload: updatedConversation,
		});

		try {
			const _business =
				typeof conversation._business === 'object'
					? conversation._business._id
					: conversation._business;

			const response = await axios.post(
				`${getWebchatApiUrl(
					window.location.href
				)}/api/conversation/toggle-pin`,
				{
					_conversation: conversation._id,
					_business,
					scope,
					pinned,
				}
			);

			if (response.data?.error) {
				dispatch({
					type: UPDATE_CUSTOMER_CONVERSATION,
					payload: conversation,
				});

				return {
					error: true,
					message:
						response.data.message ||
						'Error updating conversation pin',
				};
			}

			if (response.data?.customerConversation) {
				dispatch({
					type: UPDATE_CUSTOMER_CONVERSATION,
					payload: mergeConversationWithLocalMessages(
						response.data.customerConversation,
						updatedConversation
					),
				});
			}

			return { error: false };
		} catch {
			dispatch({
				type: UPDATE_CUSTOMER_CONVERSATION,
				payload: conversation,
			});

			return {
				error: true,
				message: 'Error updating conversation pin',
			};
		}
	};

export const resolveConversationEscalation =
	({ _customerConversation, aiPaused = false }) =>
	(dispatch, getState) => {
		const { customerConversations } = getState().webchat;
		const conv = customerConversations.find(
			(c) => c._id === _customerConversation
		);
		if (!conv) return;

		dispatch({
			type: UPDATE_CUSTOMER_CONVERSATION,
			payload: {
				...conv,
				handoffStatus: 'resolved',
				aiPaused,
				aiPauseReason: aiPaused ? 'manual_hold' : null,
			},
		});

		// Resolves with what the server stored, which can differ from what was asked:
		// a teammate's hold is kept when they had already resolved the hand-off.
		return dispatch(
			sendAiPause(_customerConversation, { aiPaused, handoffStatus: 'resolved' })
		);
	};

// Local-only counterpart to resolveConversationEscalation for a bulk resolve:
// the AI service has already written handoffStatus/aiPaused for these conversations.
export const markConversationEscalationsResolved =
	({ conversationIds, aiPaused = false }) =>
	(dispatch, getState) => {
		const ids = new Set(conversationIds);
		const customerConversations = getState().webchat?.customerConversations ?? [];
		for (const conv of customerConversations) {
			if (!ids.has(conv._id)) continue;
			dispatch({
				type: UPDATE_CUSTOMER_CONVERSATION,
				payload: {
					...conv,
					handoffStatus: 'resolved',
					aiPaused,
					aiPauseReason: aiPaused ? 'manual_hold' : null,
				},
			});
		}
	};

export const updateConversationsNotes =
	(incomingNotes) => (dispatch, getState) => {
		try {
			const { notes, _customerConversation } = incomingNotes;

			const { customerConversations } =
				getState().webchat.customerConversations;

			// this returns an array. Length = 1 if there is a matching customer, 0 if no matching customer
			const existingConversation = customerConversations.filter(
				(conversation) => conversation._id === _customerConversation
			);

			// if conversation is not here, do nothing
			if (!existingConversation || !existingConversation.length) {
				return;
			}

			// replace existing conversations notes
			const updatedConversationWithNotes = {
				...existingConversation[0],
				notes,
			};

			dispatch({
				type: UPDATE_CUSTOMER_CONVERSATION,
				payload: updatedConversationWithNotes,
			});
		} catch (err) {
			console.log(err);
		}
	};

export const updateConversationFolder =
	(incomingFolder) => (dispatch, getState) => {
		try {
			const { _folder, _customerConversation, type } = incomingFolder;

			const { customerConversations } = getState().webchat;

			if (type === 'removeAllConversationsFromFolder' && _folder) {
				dispatch(deleteFolderFromAllCustomerConversations({ _folder }));
				return;
			}

			// this returns an array. Length = 1 if there is a matching customer, 0 if no matching customer
			const existingConversation = customerConversations.filter(
				(conversation) => conversation._id === _customerConversation
			);

			// if conversation is not here, do nothing
			if (!existingConversation || !existingConversation.length) {
				return;
			}

			// replace existing conversations folder
			const updatedConversationWithFolder = {
				...existingConversation[0],
				_folder: type === 'removeFolder' ? null : _folder,
			};

			dispatch({
				type: UPDATE_CUSTOMER_CONVERSATION,
				payload: updatedConversationWithFolder,
			});
		} catch (err) {}
	};

export const updateLoadedCustomerConversationTags =
	({ _customer, tags }) =>
	(dispatch, getState) => {
		const { customerConversations } = getState().webchat;

		customerConversations
			.filter((conversation) => conversation._customer?._id === _customer)
			.forEach((conversation) => {
				dispatch({
					type: UPDATE_CUSTOMER_CONVERSATION,
					payload: {
						...conversation,
						_customer: {
							...conversation._customer,
							tags,
						},
					},
				});
			});
	};

export const deleteFolderFromAllCustomerConversations =
	({ _folder }) =>
	(dispatch, getState) => {
		const { customerConversations, assignedTypeSelected } =
			getState().webchat;

		const updatedCustomerConversations = customerConversations.map((c) => {
			if (c && c._folder && c._folder === _folder) {
				return { ...c, _folder: null };
			}
			return c;
		});

		if (assignedTypeSelected.includes('FOLDER')) {
			dispatch(setCustomerConversationAssignedType('ALL'));
		}

		dispatch({
			type: SET_CUSTOMER_CONVERSATIONS,
			payload: updatedCustomerConversations,
		});
	};

export const // when a message is sent out we need to add the customer to the customer list
	addConversationToCustomerConversations =
		(
			newConversation,
			makeActive = false // if true, it will set the newConversation to be the active conversation
		) =>
		(dispatch, getState) => {
			const { activeConversation } = getState().webchat;

			const { messages } = newConversation;

			const lastIncomingMessage = messages?.length
				? messages[messages.length - 1]
				: null;
			// Assignment / state-change events are not content the viewer just read.
			// Auto-marking here would wipe the unread flag the server just set for the recipients.
			const includesCustomerMessage = newConversation.incomingMessageId && messages?.some(
				(message) => message.sender === 'customer' && String(message._id) === String(newConversation.incomingMessageId)
			);
			const isStateChangeOnly = lastIncomingMessage?.sender === 'event' && !includesCustomerMessage;

			if (!messages || !messages.length) {
				const noMessageMessages = [
					{
						_id: 'no_message',
						updatedAt: new Date(newConversation.updatedAt),
						createdAt: new Date(newConversation.updatedAt),
						_business: '',
						_customer: '',
						_user: '',
						text: 'No messages in this conversation',
						sender: 'event',
					},
				];

				newConversation.messages = noMessageMessages;
			}

		const wasUnread =
			newConversation._id === activeConversation._id &&
			!newConversation.isRead &&
			!isStateChangeOnly;

		if (wasUnread) {
			newConversation = { ...newConversation, isRead: true };

			axios(
				`${getWebchatApiUrl(
					window.location.href
				)}/api/webchat/set-conversation-as-read`,
				{
					method: 'post',
					data: {
						_customerConversation: newConversation._id,
						_business: newConversation._business,
					},
					withCredentials: true,
				}
			);
		}

		dispatch(hydrateAiDraftsFromConversations([newConversation]));
		dispatch({
			type: UPSERT_CUSTOMER_CONVERSATION,
			payload: newConversation,
		});

		if (wasUnread) {
			const { webchat, businessGroup } = getState();
			const hasRemainingUnread = webchat.customerConversations.some(
				(c) => c._business === newConversation._business && !c.isRead && c._id !== newConversation._id
			);

			if (!hasRemainingUnread) {
				dispatch({
					type: SET_BUSINESS_GROUP,
					payload: businessGroup.map((biz) =>
						biz._id === newConversation._business
							? { ...biz, hasUnreadMessages: false }
							: biz
					),
				});
			}
		}

		if (makeActive || newConversation._id === activeConversation._id) {
			dispatch(setActiveConversation(newConversation._id));
		}
	};

// Remove a deleted customer's 1:1 conversation from the redux store so it no
// longer shows in the conversation list. Group conversations are left intact —
// removing a participant there is handled separately by refreshing the group.
export const removeCustomerConversationByCustomer =
	({ _customer }) =>
	(dispatch, getState) => {
		const { customerConversations, activeConversation } = getState().webchat;

		const conversationsToRemove = customerConversations.filter(
			(conversation) =>
				!conversation.isGroup &&
				(conversation._customer?._id || conversation._customer) ===
					_customer
		);

		conversationsToRemove.forEach((conversation) => {
			dispatch({
				type: REMOVE_CUSTOMER_CONVERSATION,
				payload: conversation._id,
			});
		});

		// Clear the active conversation if it belonged to the deleted customer
		const activeCustomerId =
			activeConversation?._customer?._id || activeConversation?._customer;

		if (activeCustomerId && activeCustomerId === _customer) {
			dispatch({
				type: SET_ACTIVE_CONVERSATION,
				payload: null,
			});
		}
	};

// if the conversation is in the redux store, we need to refresh it
export const refreshCustomerConversation =
	({ _customer }) =>
	async (dispatch, getState) => {
		try {
			const { customerConversations } = getState().webchat;

			// 1:1 conversation for this customer
			const existingConversation = customerConversations.find(
				(c) => c._customer?._id === _customer
			);

			// group conversations that include this customer as a participant —
			// their _customers array carries the (now stale) name we need to refresh
			const groupConversations = customerConversations.filter(
				(c) =>
					c.isGroup &&
					(c._customers || []).some(
						(cust) => (cust?._id || cust) === _customer
					)
			);

			if (!existingConversation && !groupConversations.length) {
				return;
			}

			const webchatApiUrl = getWebchatApiUrl(window.location.href);

			const fetchConversation = async (data) => {
				const response = await axios(
					`${webchatApiUrl}/api/webchat/get-messages-from-customer`,
					{
						method: 'post',
						data,
						withCredentials: true,
					}
				);

				return response.data;
			};

			let refreshedConversation = null;

			if (existingConversation) {
				const data = await fetchConversation({
					_customer,
					_conversation: existingConversation._id,
				});

				if (data && !data.error) {
					refreshedConversation = data.customerConversation;
					await dispatch(
						addConversationToCustomerConversations(
							data.customerConversation
						)
					);
				}
			}

			// refresh each group conversation by its own id (groups are keyed by
			// _customers, not _customer); preserve the already-loaded messages so the
			// open thread doesn't flicker — we only need the refreshed participants
			await Promise.all(
				groupConversations.map(async (groupConversation) => {
					const data = await fetchConversation({
						_customerConversation: groupConversation._id,
					});

					if (!data || data.error) {
						return;
					}

					await dispatch(
						addConversationToCustomerConversations({
							...data.customerConversation,
							messages:
								groupConversation.messages ||
								data.customerConversation.messages,
						})
					);
				})
			);

			return refreshedConversation;
		} catch (error) {
			console.log(
				'Error getting customer conversation for customer',
				error
			);
			return null;
		}
	};

// Batch refresh conversations for multiple customers - only refreshes conversations that are already loaded
export const batchRefreshCustomerConversations =
	({ customerIds }) =>
	async (dispatch, getState) => {
		try {
			const { customerConversations } = getState().webchat;

			// Filter to only customers that have conversations already loaded in Redux
			// (group conversations have _customers[] and no _customer — skip them)
			const conversationsToRefresh = customerConversations.filter((c) =>
				customerIds.includes(c._customer?._id)
			);

			if (conversationsToRefresh.length === 0) {
				return [];
			}

			// Refresh all relevant conversations in parallel
			const refreshPromises = conversationsToRefresh.map((conversation) =>
				dispatch(
					refreshCustomerConversation({
						_customer: conversation._customer._id,
						_conversation: conversation._id,
					})
				)
			);

			const results = await Promise.all(refreshPromises);
			return results.filter((result) => result !== null);
		} catch (error) {
			console.log('Error batch refreshing customer conversations', error);
			return [];
		}
	};

export const addMessageToCustomerConversation =
	(message) => (dispatch, getState) => {
		const { customerConversations } = getState().webchat;
		const { _customer } = message;
		const conversation = customerConversations.find(
			(c) => c._customer === _customer
		);
		if (!conversation) {
			return;
		}
		const newConversation = {
			...conversation,
			messages: [...conversation.messages, message],
		};
		dispatch({
			type: UPDATE_CUSTOMER_CONVERSATION,
			payload: newConversation,
		});
	};

export const locallyAddFolderToCustomerConversation =
	({ _conversation, _folder }) =>
	(dispatch, getState) => {
		const { customerConversations } = getState().webchat;

		const updatedCustomerConversations = customerConversations.map((c) => {
			if (c?._id && c._id === _conversation) {
				return { ...c, _folder };
			}
			return c;
		});

		const updatedConversation = updatedCustomerConversations.find(
			(c) => c._id === _conversation
		);

		dispatch({
			type: UPDATE_CUSTOMER_CONVERSATION,
			payload: updatedConversation,
		});
	};

export const removeFolderFromCustomerConversation =
	({ _conversation }) =>
	(dispatch, getState) => {
		const { customerConversations } = getState().webchat;

		const updatedCustomerConversations = customerConversations.map((c) => {
			if (c?._id && c._id === _conversation) {
				return { ...c, _folder: null };
			}
			return c;
		});

		const updatedConversation = updatedCustomerConversations.find(
			(c) => c._id === _conversation
		);

		dispatch({
			type: UPDATE_CUSTOMER_CONVERSATION,
			payload: updatedConversation,
		});
	};

export const setActiveDashboard = (activeDashboard) => (dispatch) => {
	dispatch({
		type: ACTIVE_DASHBOARD,
		payload: activeDashboard,
	});
};

export const updateConversationAssignees =
	({ _conversation, _assignees, _department }) =>
	(dispatch, getState) => {
		const { customerConversations } = getState().webchat;
		const me = getState().auth.user._id;

		const existingConversation = customerConversations.find(
			(c) => c._id === _conversation
		);

		if (!existingConversation) {
			return;
		}

		// Mirror the server: an assignment that reaches anyone other than me resets
		// read state for everyone except me, so the row does not keep a stale isRead.
		const reachesSomeoneElse =
			!!_department || (_assignees || []).some((id) => id !== me);

		const updatedConversation = {
			...existingConversation,
			_assignees: _assignees || [],
			_department: _department || null,
			// Clear legacy _assignee when using new fields
			_assignee: null,
			...(reachesSomeoneElse ? { isRead: false, _readByUsers: [me] } : {}),
		};

		dispatch({
			type: UPDATE_CUSTOMER_CONVERSATION,
			payload: updatedConversation,
		});
	};
